import React from 'react'
import { resultTileDefaultProps, resultTileProps } from './ResultTile.proptypes'
import { entryPointId, TILE_HAS_EXACT_DISTANCE } from 'settings/app/constants'

// libs && others
import {
  Info2,
  Home2,
  Location,
  Phone2,
  Envelop,
  Wegstrecke,
  Display4,
  ArrowDown4,
  ArrowRightOutline,
} from '@aok/icon-functional'
import * as Component from './ResultTile.styles'
import { ErrorBoundary, useTextContext, ResultTileProvider } from 'hocs'
import { LocalisationProvider } from 'hooks'

// component stack
import {
  ResultTileTitle,
  AccordionTitle,
  AccordionContent,
} from './helpers/ResultTileContentFactory'
import AccordionRowsFactory from './helpers/AccordionRowsFactory'
import {
  BookmarkToggle,
  CalculateRouteBtn,
  Icon,
  Tooltip,
} from 'components/atoms'

import { TextLinkImage } from 'components/molecules'
import { isRedactedString } from 'settings/app/helpers'

/**
 * result tile
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const ResultTile = (props) => {
  const {
    isMobile,
    open,
    active,
    type,
    isBooked,
    setBooked,
    isBookable,
    showDistance,
    handleHover,
    linkComponent,
    linkComponentProps,
    result,
    generalTooltips,
    showDataRows,
    ...other
  } = props

  const textContext = useTextContext()
  const [show, setShow] = React.useState(open)
  const [enableAccordion, setEnableAccordion] = React.useState(false)
  const { distance, benefits, phone, fax, address, docType, locationDistance } =
    result

  React.useEffect(() => {
    setShow(open)
  }, [open])

  // prop mapping for resultTile content factory method
  result.title = result.locationName ? result.locationName : result.name
  result.type = type

  // render flags && handler
  const renderAccordion =
    type === 'hospitals' ||
    type === 'doctors' ||
    type === 'oncallservices' ||
    type === 'asv_teams'

  const noOpeningHours = result?.openIndicator?.state === 'no_information'
  const renderAokBenefits =
    (type === 'hospitals' ||
      type === 'doctors' ||
      type === 'careservices' ||
      type === 'carehomes' ||
      type === 'dayandnightcare') &&
    !!benefits

  const renderDetailLink =
    'ambulances' !== type &&
    'asvTeam' !== docType &&
    'midwives' !== type &&
    'oncallservices' !== type &&
    'caresupport' !== type

  const renderRouteButton =
    (type === 'ambulances' || type === 'oncallservices') &&
    result.coordinates !== null

  const appendResultTileDetailLink =
    type === entryPointId.HOSPITALS ||
    (type === entryPointId.DOCTORS && 'asvTeam' !== docType)

  const tileRef = React.createRef()

  // smaller result tile header && tile body class
  const appendNoMinHeaderClass =
    type === 'midwives' || type === 'carehomes' || type === 'careservices'
  const noAccordionContainerClass =
    type === 'midwives' || type === 'ambulances'
      ? 'no-accordion-container'
      : 'no-accordion-container small-padding'

  const getBenefits = (benefits) =>
    benefits.map((item, index) => (
      <TextLinkImage
        key={ index }
        headline={ item.headline }
        copy={ item.copy }
        moreLink={
          item.moreLink ? { url: item.moreLink, target: '_blank' } : null
        }
        showCookieHeader={ index === 0 }
      />
    ))

  const getEmail = () => (
    <div className='address-row'>
      <span data-cy-email={ result.email }>
        <Envelop className='address-icon' />
        {!isMobile ? (
          <a
            href={ `mailto:${ result.email }` }
            target='_blank'
            rel='noopener noreferrer'>
            {result.email}
          </a>
        ) : (
          result.email
        )}
      </span>
    </div>
  )

  const getFax = () => (
    <span className='address-container' data-cy-fax={ fax }>
      <Icon className='icon-f-printer' />
      {fax}
    </span>
  )

  const getDistanceIcon = () =>
    locationDistance &&
    (type === 'careservices' || type === 'dayandnightcare') ? (
      <Wegstrecke className='address-icon icon-grey' />
    ) : (
      <Location className='address-icon icon-grey' />
    )

  const getDistance = () => {
    const distStr = `${ Number(distance / 1000).toFixed(2) } km`
    return <span data-cy={ 'distance-string' }>{distStr}</span>
  }

  // to avoid split html elements
  const getAddressAsString = () =>
    !(
      isRedactedString(address.street) ||
      isRedactedString(address.streetAddition)
    )
      ? `${ address.street.trim() }${
          address.streetAddition && ` ${ address.streetAddition }`
        }, `
      : null
  const getCityAsString = () =>
    !(isRedactedString(address.zip || '') || isRedactedString(address.city))
      ? `${ address.zip && address.zip } ${ address.city }`
      : null

  // for header structure
  const LinkWrapper = isMobile ? linkComponent : 'span'

  // wrapper class
  const getHeadlineWrapperClass = () => {
    let headlineClass = 'headline'

    // shorter h4 class (care services can have right benefits and location distance)
    if (
      TILE_HAS_EXACT_DISTANCE.includes(type) &&
      !!result?.locationDistance &&
      !!result?.benefits
    ) {
      headlineClass += ' two-add-value'
    } else if (
      !!result?.benefits ||
      (!result?.benefits && !!result?.locationDistance)
    ) {
      headlineClass += ' one-add-value'
    }

    return headlineClass
  }

  return (
    <LocalisationProvider>
      <ResultTileProvider values={ props }>
        <Component.Container
          data-id={ result.id }
          data-cy-title={ result.title }
          data-cy-tile-type={ result.type }
          ref={ tileRef }
          isMobile={ isMobile }
          active={ active }
          isBooked={ isBooked }
          onMouseEnter={ () => handleHover && handleHover(true) }
          onMouseLeave={ () => handleHover && handleHover(false) }
          onFocus={ () => handleHover && handleHover(true) }
          onBlur={ () => handleHover && handleHover(false) }
          clickAbleHeader={ appendResultTileDetailLink }
          renderAokBenefits={ renderAokBenefits }
          { ...other }>
          {isBookable && isMobile && (
            <BookmarkToggle
              className='mobile-toggle'
              selected={ isBooked }
              onToggle={ () => setBooked && setBooked() }
            />
          )}

          <LinkWrapper { ...(isMobile && linkComponentProps) }>
            <div
              className={ `result-tile-header ${
                appendNoMinHeaderClass ? 'no-min-height' : ''
              }` }>
              <div className='content'>
                <div
                  className={ getHeadlineWrapperClass() }
                  data-cy='result-tile-headline'>
                  <ResultTileTitle { ...props } />
                </div>

                <div className='address'>
                  {type === 'doctors' &&
                    docType === 'doctor' &&
                    result.onlyPrivatePatients.onlyPrivatePatients && (
                      <div className='address-row'>
                        <span className='address-container'>
                          <Info2 className='address-icon icon-grey' />
                          {result.onlyPrivatePatients.label}
                          <Tooltip
                            arrow='middle'
                            className='tooltip'
                            dialogTitle={ result.onlyPrivatePatients.label }
                            infoIconId='privat-patients-chip-icon-id'
                            tooltipId='privat-patients-tip-id'>
                            {result.onlyPrivatePatients.tip}
                          </Tooltip>
                        </span>
                      </div>
                    )}

                  {/* address / distance / phone (shown one below the other) */}
                  {(type === 'hospitals' || type === 'doctors') && !isMobile && (
                    <div className='address-column'>
                      {address !== null && (
                        <>
                          <span className='address-row'>
                            <Home2 className='address-icon' />
                            {getAddressAsString()}
                            {getCityAsString()}
                          </span>
                          <span className='address-row'>
                            {showDistance && distance > 0 && (
                              <>
                                <span
                                  data-cy-address-distance-km={ Number(
                                    distance / 1000
                                  ).toFixed(2) }
                                />
                                {getDistanceIcon()}
                                {getDistance()}
                              </>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  {type === 'midwives' && (
                    <div className='midwives-contact'>
                      {phone && !isRedactedString(phone) && (
                        <div className='address-row'>
                          <span data-cy-phone={ phone }>
                            <Phone2 className='address-icon' />
                            {phone}
                          </span>
                        </div>
                      )}
                      {result.email &&
                        !isRedactedString(result.email) &&
                        getEmail()}
                      {showDistance && distance > 0 && (
                        <div className='address-row'>
                          <span
                            data-cy-address-distance-km={ Number(
                              distance / 1000
                            ).toFixed(2) }>
                            {getDistanceIcon()}
                            {getDistance()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* address && distance (shown in one line) */}
                  {(type === 'dayandnightcare' ||
                    type === 'caresupport' ||
                    type === 'careservices' ||
                    type === 'carehomes' ||
                    type === 'emergencies' ||
                    type === 'oncallservices' ||
                    type === 'ambulances' ||
                    ((type === 'hospitals' || type === 'doctors') &&
                      isMobile)) && (
                    <div className='address-row'>
                      {address !== null && (
                        <span className='address-container'>
                          <Home2 className='address-icon' />
                          <span>
                            {getAddressAsString()}
                            <span className='address-zip-city'>
                              {getCityAsString()}
                            </span>
                          </span>
                        </span>
                      )}
                      {showDistance && distance > 0 && (
                        <div
                          className='address-distance'
                          data-cy='distance'
                          data-cy-address-distance-km={
                            locationDistance ||
                            Number(distance / 1000).toFixed(2)
                          }>
                          {getDistanceIcon()}
                          {locationDistance ? (
                            <span data-cy='detail-view-search-location-distance'>{`${ locationDistance } km`}</span>
                          ) : (
                            getDistance()
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {type !== 'midwives' && (
                    <>
                      <div
                        className={ `address-row ${
                          renderRouteButton ? 'with-route-btn' : ''
                        }` }>
                        {phone && !isRedactedString(phone) && (
                          <span className='address-container'>
                            <Phone2 className='address-icon' />
                            {phone}
                          </span>
                        )}

                        {renderRouteButton && !isMobile && (
                          <CalculateRouteBtn
                            marker={ result.coordinates }
                            className='result-tile-navigation-btn'
                            type='LINK'
                          />
                        )}

                        {renderAokBenefits && isMobile && (
                          <div className='benefit is-right'>
                            <div className='benefit-text'>
                              {textContext['resultTile.aok.benefits']}
                            </div>
                            <Tooltip
                              arrow='left'
                              infoIconId='info-icon'
                              tooltipId='tooltip'>
                              {getBenefits(benefits)}
                            </Tooltip>
                          </div>
                        )}
                      </div>

                      {fax &&
                        !isRedactedString(fax) /*  || locationDistance */ && (
                          <div className='address-row'>
                            {fax && getFax()}
                            {/* {locationDistance && isMobile && getSearchLocation()} */}
                          </div>
                        )}

                      <>
                        {result.email &&
                          !isRedactedString(result.email) &&
                          getEmail()}
                        {result.website && (
                          <div className='address-row'>
                            <span data-cy-website={ result.website }>
                              <Display4 className='address-icon' />
                              {!isMobile ? (
                                <a
                                  href={ `${
                                    result.website.match(/^(http)s?:\/\/.*/)
                                      ? ''
                                      : 'http://'
                                  }${ result.website }` }
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  {result.website}
                                </a>
                              ) : (
                                result.website
                              )}
                            </span>
                          </div>
                        )}
                      </>
                    </>
                  )}
                </div>
              </div>

              {/* search location && aok benefits and bookmark item */}
              <div className='header-row'>
                {/* {locationDistance && !isMobile && getSearchLocation()} */}

                {renderAokBenefits && !isMobile && (
                  <div className='benefit'>
                    <div className='benefit-text'>
                      {textContext['resultTile.aok.benefits']}
                    </div>
                    <Tooltip
                      arrow='right'
                      infoIconId='info-icon'
                      closeOnClickOutside={ true }
                      tooltipId='tooltip'>
                      {getBenefits(benefits)}
                    </Tooltip>
                  </div>
                )}
                {isBookable && !isMobile && (
                  <BookmarkToggle
                    selected={ isBooked }
                    onToggle={ (e) => setBooked && setBooked(e) }
                  />
                )}
              </div>

              {!isMobile && renderDetailLink && (
                <Component.DetailLink
                  component={ linkComponent }
                  title={ textContext['general.showDetailView'] }
                  data-cy='show-detail-view'
                  { ...linkComponentProps }>
                  {active && (
                    <span className='DetailLinkText'>
                      {
                        textContext[
                          `resultTile.detailLink.${
                            entryPointId.HOSPITALS === type
                              ? 'hospitals'
                              : 'doctors'
                          }`
                        ]
                      }
                    </span>
                  )}
                  <Component.DetailIcon
                    icon={ <ArrowRightOutline /> }
                    size={ 32 }
                  />
                </Component.DetailLink>
              )}
            </div>
          </LinkWrapper>

          {/* accordion part - abs1 - dynamic tile data */}
          {renderAccordion && (
            <Component.Accordion>
              <Component.AccordionPanelButton
                aria-expanded={ show }
                onClick={ () =>
                  enableAccordion && setShow((prevState) => !prevState)
                }
                tabIndex='0'
                data-accordion-trigger
                data-cy='accordion'
                enabled={ enableAccordion }>
                <ErrorBoundary>
                  <AccordionTitle { ...props } />
                  <ArrowDown4 className='accordion-icon' />
                </ErrorBoundary>
              </Component.AccordionPanelButton>

              {!noOpeningHours && (
                <Component.AccordionPanelBody className={ !show && 'collapsed' }>
                  <Component.AccordionContentWrapper
                    withOutPadding={ 'doctors' === type && 'asvTeam' === docType }
                    ref={ (element) =>
                      setEnableAccordion(element?.hasChildNodes())
                    }
                    data-cy='accordion-body'>
                    <AccordionContent { ...props } />
                  </Component.AccordionContentWrapper>
                </Component.AccordionPanelBody>
              )}
            </Component.Accordion>
          )}

          {/* no accordion part - abs2 */}
          {!renderAccordion && showDataRows && (
            <>
              {/* midwives - service */}
              {type === 'midwives' &&
                Array.isArray(result.services) &&
                result.services.length > 0 && (
                  <div className={ noAccordionContainerClass }>
                    <AccordionRowsFactory rowType={ 'listDataRow' } />
                  </div>
                )}

              {/* TBD: services is also midwives */}
              {/* caresupport - service/price per hour */}
              {type === 'caresupport' &&
                (Array.isArray(result.services) || result.pricePerHour) && (
                  <div className={ noAccordionContainerClass }>
                    {Array.isArray(result.services) && (
                      <AccordionRowsFactory rowType={ 'listDataRow' } />
                    )}
                    {result.pricePerHour && (
                      <AccordionRowsFactory rowType={ 'pricePerHour' } />
                    )}
                  </div>
                )}

              {/* ambulances - levels */}
              {type === 'ambulances' && Array.isArray(result.levels) && (
                <div className={ noAccordionContainerClass }>
                  <AccordionRowsFactory rowType={ 'listDataRow' } />
                </div>
              )}

              {/* carehomes - transparency report and contribution */}
              {(type === 'carehomes' ||
                (type === 'careservices' &&
                  (result.transparencyReport || !!result.contribution))) && (
                <div className={ noAccordionContainerClass }>
                  {result.transparencyReport && (
                    <AccordionRowsFactory rowType={ 'transparencyReport' } />
                  )}
                  {/* (type === 'carehomes') && (
                    <AccordionRowsFactory onClickHandler={() => alert('IMPLEMENTATION NOT DONE YET')} rowType={"quality"}/>
                  ) */}
                  {!!result.contribution && (
                    <AccordionRowsFactory rowType={ 'contribution' } />
                  )}
                </div>
              )}

              {/* dayandnightcare - contribution */}
              {type === 'dayandnightcare' && !!result.contribution && (
                <div className={ noAccordionContainerClass }>
                  <AccordionRowsFactory rowType={ 'contribution' } />
                </div>
              )}
            </>
          )}
        </Component.Container>
      </ResultTileProvider>
    </LocalisationProvider>
  )
}

ResultTile.defaultProps = { ...resultTileDefaultProps }
ResultTile.propTypes = { ...resultTileProps }

export default React.memo(ResultTile)
